const HIDDEN_ROUTES = new Set(['/barang'])

const MASTER_ROUTES = [
  '/asset',
  '/asset-kategori',
  '/asset-blok',
]

const OPERASIONAL_ROUTES = [
  '/asset-pengadaan',
  '/asset-penyimpanan',
  '/asset-peminjaman',
  '/asset-pengembalian',
  '/asset-mutasi-request',
  '/asset-audit',
]

const PERBAIKAN_ROUTE = '/asset-disposal/perbaikan'

const ALL_GROUP_ROUTES = new Set([
  ...MASTER_ROUTES,
  ...OPERASIONAL_ROUTES,
  PERBAIKAN_ROUTE,
])

const ICON_BY_ROUTE = {
  '/asset': 'PackageIcon',
  '/asset-kategori': 'TagIcon',
  '/asset-blok': 'GridIcon',
  '/asset-pengadaan': 'FilePlusIcon',
  '/asset-penjualan': 'ShoppingCartIcon',
  '/asset-laporan': 'FileTextIcon',
}

const CREATED_GROUP_TITLES = new Set(['Master Aset', 'Operasional Aset'])

function getRoute(item) {
  return item?.route || item?.url || null
}

function getMenuItemKey(item) {
  return getRoute(item) || item.header || item.title
}

function applyIcon(item) {
  const route = getRoute(item)
  if (!route || !ICON_BY_ROUTE[route]) return item
  return { ...item, icon: ICON_BY_ROUTE[route] }
}

function applyIconsDeep(menus) {
  return menus.map(item => {
    if (item.children?.length) {
      return {
        ...item,
        children: item.children.map(child => applyIcon(child)),
      }
    }
    return applyIcon(item)
  })
}

function collectReservedRoutes(menus) {
  const reserved = new Set()
  menus.forEach(item => {
    if (!item.children?.length) return
    item.children.forEach(child => {
      const route = getRoute(child)
      if (route) reserved.add(route)
    })
  })
  return reserved
}

function indexFlatItems(menus) {
  const map = new Map()
  menus.forEach(item => {
    if (item.children?.length) return
    const route = getRoute(item)
    if (!route) return
    if (map.has(route)) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[groupAdminAssetMenus] duplicate flat route ignored: ${route}`)
      }
      return
    }
    map.set(route, item)
  })
  return map
}

function pickChildren(routes, flatIndex, reservedRoutes, usedRoutes) {
  const children = []
  routes.forEach(route => {
    if (reservedRoutes.has(route) || usedRoutes.has(route)) return
    const item = flatIndex.get(route)
    if (!item) return
    usedRoutes.add(route)
    children.push({
      ...applyIcon(item),
      key: getMenuItemKey(item),
    })
  })
  return children
}

function buildGroup(title, icon, children) {
  const tag = children.reduce((sum, child) => sum + (parseInt(child.tag, 10) || 0), 0)
  return {
    title,
    icon,
    children,
    key: title,
    ...(tag > 0 ? { tag } : {}),
  }
}

function isDisposalGroup(item) {
  if (!item?.children?.length) return false
  const title = (item.title || '').toLowerCase()
  if (title.includes('disposal')) return true
  const route = getRoute(item)
  return route === '/asset-disposal-wrap'
}

function mergePerbaikanIntoDisposal(menus, flatIndex, usedRoutes) {
  const perbaikan = flatIndex.get(PERBAIKAN_ROUTE)
  if (!perbaikan || usedRoutes.has(PERBAIKAN_ROUTE)) {
    return menus
  }

  let merged = false
  const result = menus.map(item => {
    if (!isDisposalGroup(item) || merged) return item

    const alreadyHas = item.children.some(
      child => getRoute(child) === PERBAIKAN_ROUTE,
    )
    if (alreadyHas) {
      usedRoutes.add(PERBAIKAN_ROUTE)
      merged = true
      return item
    }

    usedRoutes.add(PERBAIKAN_ROUTE)
    merged = true
    return {
      ...item,
      children: [
        ...item.children,
        {
          ...applyIcon(perbaikan),
          key: getMenuItemKey(perbaikan),
        },
      ],
    }
  })

  return result
}

function dedupeMenuTree(menus) {
  const seen = new Set()
  const result = []

  menus.forEach(item => {
    if (item.children?.length) {
      if (!CREATED_GROUP_TITLES.has(item.title)) {
        item.children.forEach(child => {
          const route = getRoute(child)
          if (route) seen.add(route)
        })
        result.push(item)
        return
      }

      const children = []
      item.children.forEach(child => {
        const route = getRoute(child)
        if (route && seen.has(route)) return
        if (route) seen.add(route)
        children.push(child)
      })
      if (children.length > 0) {
        result.push({ ...item, children })
      }
      return
    }

    const route = getRoute(item)
    if (route && seen.has(route)) return
    if (route) seen.add(route)
    result.push(item)
  })

  return result
}

/**
 * Transform Admin Asset sidebar:
 * - hide /barang
 * - group Master Aset + Operasional Aset
 * - move flat Perbaikan into Disposal Aset group
 * - override broken XCircle icons for known asset routes
 */
export default function groupAdminAssetMenus(menus) {
  if (!Array.isArray(menus) || menus.length === 0) {
    return menus
  }

  const filtered = menus.filter(item => {
    const route = getRoute(item)
    return !route || !HIDDEN_ROUTES.has(route)
  })

  const reservedRoutes = collectReservedRoutes(filtered)
  const flatIndex = indexFlatItems(filtered)
  const usedRoutes = new Set()

  const masterChildren = pickChildren(
    MASTER_ROUTES,
    flatIndex,
    reservedRoutes,
    usedRoutes,
  )
  const operasionalChildren = pickChildren(
    OPERASIONAL_ROUTES,
    flatIndex,
    reservedRoutes,
    usedRoutes,
  )

  const feGroups = []
  if (masterChildren.length > 0) {
    feGroups.push(buildGroup('Master Aset', 'BoxIcon', masterChildren))
  }
  if (operasionalChildren.length > 0) {
    feGroups.push(buildGroup('Operasional Aset', 'ArchiveIcon', operasionalChildren))
  }

  let groupsInserted = false
  const output = []

  const insertGroups = () => {
    if (groupsInserted || feGroups.length === 0) return
    feGroups.forEach(group => output.push(group))
    groupsInserted = true
  }

  filtered.forEach(item => {
    const route = getRoute(item)

    if (!groupsInserted) {
      const isFirstGroupedFlat = !item.children?.length
        && route
        && ALL_GROUP_ROUTES.has(route)
      const isDisposal = isDisposalGroup(item)
      if (isFirstGroupedFlat || isDisposal) {
        insertGroups()
      }
    }

    if (item.children?.length) {
      output.push(item)
      return
    }

    if (route && usedRoutes.has(route)) {
      return
    }

    // Skip perbaikan here; merged into Disposal below (or kept flat if no Disposal group)
    if (route === PERBAIKAN_ROUTE) {
      return
    }

    output.push(item)
  })

  if (!groupsInserted) {
    insertGroups()
  }

  let withDisposal = mergePerbaikanIntoDisposal(output, flatIndex, usedRoutes)

  // If Disposal group missing, keep Perbaikan as flat item
  if (flatIndex.has(PERBAIKAN_ROUTE) && !usedRoutes.has(PERBAIKAN_ROUTE)) {
    const perbaikan = applyIcon(flatIndex.get(PERBAIKAN_ROUTE))
    const penjualanIdx = withDisposal.findIndex(
      item => getRoute(item) === '/asset-penjualan',
    )
    if (penjualanIdx >= 0) {
      withDisposal = [
        ...withDisposal.slice(0, penjualanIdx),
        perbaikan,
        ...withDisposal.slice(penjualanIdx),
      ]
    } else {
      withDisposal = [...withDisposal, perbaikan]
    }
    usedRoutes.add(PERBAIKAN_ROUTE)
  }

  return applyIconsDeep(dedupeMenuTree(withDisposal))
}
